import { inject, injectable } from 'inversify';
import { autorun, makeAutoObservable } from 'mobx';
import {
  getAccountTypes,
  getAccuralMethods,
  getAreaTypes,
  getBanks,
  getDocTypes,
  getHouseStates,
  getKnp,
  getServiceCompanies,
  getServiceGroups,
  getUnionTypes
} from '@shared/api/dictionaries';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import {
  AccountType,
  AccuralMethods,
  AreaType,
  Bank,
  DocType,
  HouseState,
  Knp,
  ServiceCompany,
  ServiceGroup,
  UnionType
} from '@shared/types/dictionaries';
import { getValuesByKeys } from '@shared/api/keyValues';
import { links } from '@shared/instructions/config';
import { AuthServiceIdentifier, type AuthService } from '@shared/types/mobx/services/auth';

@injectable()
export class DictionaryModule {
  accountTypes: AccountType[] = [];

  accuralMethods: AccuralMethods[] = [];

  areaTypes: AreaType[] = [];

  banks: Bank[] = [];

  docTypes: DocType[] = [];

  houseStates: HouseState[] = [];

  knp: Knp[] = [];

  serviceCompanies: ServiceCompany[] = [];

  serviceGroups: ServiceGroup[] = [];

  unionTypes: UnionType[] = [];

  instructionLinks: Record<string, string> = {};

  isCatalogLoaded = false;

  constructor(@inject(AuthServiceIdentifier) private authModule: AuthService) {
    makeAutoObservable(this);

    this.loadInstructionLinks();

    autorun(() => {
      if (this.authModule.userData?.id) {
        this.loadDictionaries();
      }
    });
  }

  loadDictionaries = async (): Promise<void> => {
    // account
    try {
      this.accountTypes = await getAccountTypes();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // accural
    try {
      this.accuralMethods = await getAccuralMethods();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // area
    try {
      this.areaTypes = await getAreaTypes();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // banks
    try {
      this.banks = await getBanks();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // docTypes
    try {
      this.docTypes = await getDocTypes();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // houseStates
    try {
      this.houseStates = await getHouseStates();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // knp
    try {
      this.knp = await getKnp();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // serviceCompanies
    try {
      this.serviceCompanies = await getServiceCompanies();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // serviceGroups
    try {
      this.serviceGroups = await getServiceGroups();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }

    // unionTypes
    try {
      this.unionTypes = await getUnionTypes();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }
  };

  loadInstructionLinks = async () => {
    try {
      this.instructionLinks = await this.getInstructionLinks();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }
  };

  getInstructionLinks = () => {
    const keys = Object.keys(links);

    return getValuesByKeys(keys);
  };
}
